// Libraries
import router from '@/plugins/router'
import { i18n } from '@/plugins/i18n'
import { apollo } from '@/plugins/apollo'

// graphql files
// import validate from 'GraphQL/mutations/profile/validate.gql'
import login from 'GraphQL/mutations/session/login.gql'
import logout from 'GraphQL/mutations/session/logout.gql'
import getSession from 'GraphQL/queries/session/getSession.gql'

// Turns backend validation errors ({ field: [{ code, expected }] }) into a readable line
const formatErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return ''

  return Object.entries(errors)
    .map(([field, fieldErrors]) => {
      const details = [].concat(fieldErrors).map(e => {
        if (!e || typeof e !== 'object') return String(e)
        return e.expected !== undefined ? `${e.code} (${e.expected})` : e.code
      })
      return `${field}: ${details.join(', ')}`
    })
    .join('; ')
}

// Extracts a meaningful message from Apollo / network errors
const describeError = (err) => {
  if (!err) return ''
  if (err.graphQLErrors && err.graphQLErrors.length) {
    return err.graphQLErrors.map(e => e.message).join('; ')
  }
  if (err.networkError) {
    const { result, message } = err.networkError
    if (result && result.errors && result.errors.length) {
      return result.errors.map(e => e.message).join('; ')
    }
    return message
  }
  return err.message || String(err)
}

export default {
  async logout ({ commit, state }) {
    if (!state.token.token) {
      localStorage.removeItem('token.token')
      localStorage.removeItem('token.validBefore')
      commit('clearEntity')
      router.push('/Login')
      return
    }

    await commit('setLoading', true, { root: true })
    await apollo.mutate({
      mutation: logout,
      variables: {
        apiToken: state.token.token
      }
    }).then(_ => {
      localStorage.removeItem('token.token')
      localStorage.removeItem('token.validBefore')
      commit('clearEntity')
      router.push('/Login')
    }).catch(err => {
      console.error(err)
      commit('toggleSnackbar', undefined, { root: true })
    }).finally(() => {
      commit('setLoading', false, { root: true })
    })
  },

  async login ({ dispatch, commit }, payload) {
    commit('setLoading', true, { root: true })

    await apollo.mutate({
      mutation: login,
      variables: {
        username: payload.username,
        password: payload.password
      }
    }).then(response => {
      const { status, errors, result } = response.data.adminLogin
      switch (status) {
        case 'OK':
          // console.log(result)
          localStorage.setItem('token.token', result.apiToken)
          // localStorage.setItem('token.validBefore', result.token.validBefore)
          commit('setEntity', result)
          return dispatch('loadData')
        case 'BADREQUEST':
          commit('toggleSnackbar', {
            message: i18n.t('session.login.badRequest'),
            caption: formatErrors(errors),
            color: 'warning'
          }, { root: true })
          break
        case 'ERROR':
        case 'NOTFOUND':
        case 'ACCESSDENIED':
          commit('toggleSnackbar', {
            message: i18n.t('session.login.invalidCredentials'),
            caption: formatErrors(errors),
            color: 'warning'
          }, { root: true })
          break
        default:
          console.error('Unexpected adminLogin response', response.data.adminLogin)
          commit('toggleSnackbar', {
            message: i18n.t('helpers.errors.disaster'),
            caption: formatErrors(errors) || status,
            color: 'red darken-4'
          }, { root: true })
          break
      }
    }).catch(err => {
      console.error(err)
      commit('toggleSnackbar', {
        message: i18n.t('helpers.errors.disaster'),
        caption: describeError(err),
        color: 'red darken-4'
      }, { root: true })
    }).finally(() => {
      commit('setLoading', false, { root: true })
    })
  },

  async loadEntity ({ dispatch, commit }, payload) {
    let apiToken = localStorage.getItem('token.token')
    const url = new URL(window.location.href)
    if (url.hash.includes('api_token')) {
      const paramsString = url.hash.split('#')[1]
      const params = paramsString.split('&')

      // For parms to search for a api_token
      const apiTokenLoginAs = params.find(param => param.includes('api_token')).split('=')[1]
      if (apiTokenLoginAs) {
        apiToken = apiTokenLoginAs
      }
    }
    if (!apiToken) {
      router.push('/Login')
      commit('setLoading', false, { root: true })
      return
    }

    await apollo.query({
      query: getSession,
      variables: {
        apiToken
      }
    }).then(response => {
      const { status, result } = response.data.getAdminSession

      switch (status) {
        case 'OK':
          localStorage.setItem('token.token', result.apiToken)
          if (result.language) {
            commit('setLanguage', result.language.code, { root: true })
          }
          commit('setEntity', result)

          if (!payload) {
            dispatch('loadData', payload)
          }
          break
        case 'BADREQUEST':
          commit('toggleSnackbar', { message: i18n.t('errors.not_found'), color: 'warning' }, { root: true })
          break
        case 'NOTFOUND':
          commit('toggleSnackbar', { message: i18n.t('session.expired'), color: 'orange darken-2' }, { root: true })
          dispatch('logout')
          break
        case 'INTERNALERROR':
          commit('toggleSnackbar', null, { root: true })
          break
        default:
          router.push('/Login')
          break
      }
    }).catch(err => {
      console.error(err)
      commit('toggleSnackbar', undefined, { root: true })
    }).finally(() => {
      commit('setLoading', false, { root: true })
    })
  },
  // async loadData({ dispatch, commit }, payload) {
  async loadData ({ commit, dispatch }, payload) {
    const lastUrl = window.location.href.split('#')[1]
    // await dispatch('dtcs/getList', false, { root: true })
    // await dispatch('events/getList', false, { root: true })
    // await dispatch('dailyOdometers/getList', false, { root: true })
    await dispatch('users/getList', false, { root: true })
    await dispatch('configurations/details', false, { root: true })
    await dispatch('wialonUsers/getList', false, { root: true })
    await dispatch('drivers/getList', false, { root: true })
    await dispatch('mechanics/getList', false, { root: true })
    await dispatch('trailerProfileTemplates/getList', false, { root: true })
    await dispatch('vehicleTypes/getList', false, { root: true })
    // await dispatch('geofences/getList', false, { root: true })
    await dispatch('groups/getList', false, { root: true })
    // await dispatch('newReports/getList', false, { root: true })
    await dispatch('apiTokens/getList', false, { root: true })
    // await dispatch('notifications/getList', false, { root: true })
    // await dispatch('unitDocuments/getList', false, { root: true })
    // await dispatch('driverDocuments/getList', false, { root: true })
    await commit('setDataHasLoaded', true, { root: true })
    await commit('setLoading', false, { root: true })

    let redirect = '/Home'
    if (payload === undefined) {
      redirect = lastUrl === '/Login' ? '/Home' : lastUrl
    }

    if (router.currentRoute.fullPath !== redirect) {
      router.push(redirect).catch(err => {
        if (err.name !== 'NavigationDuplicated') throw err
      })
    }
  }
}
